import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ShieldCheck, ArrowRight, EyeSlash, Eye, LockKey, EnvelopeSimple,
  Sparkle, ArrowLeft, Globe, Pulse, ShieldStar, ArrowsClockwise,
} from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Roles allowed to log in via /adentry. Per access-control rules,
// /adentry is exclusively for the admin console — gov users must use
// /gov/login, customers /me/login, etc.
const ADMIN_ENTRY_ROLES = new Set([
  "superadmin", "admin", "manager",
  "office_staff", "office", "hr", "service_coordinator",
]);

function rejectIfNotAdminEntry(user, setErr) {
  if (!user) return false;
  if (ADMIN_ENTRY_ROLES.has(user.role)) return false;
  // Clear any session that got stored before we could reject.
  localStorage.removeItem("rescigo_token");
  localStorage.removeItem("rescigo_user");
  const portal = user.role === "govt" ? "/gov/login"
              : user.role === "customer" ? "/me/login"
              : user.role === "staff" ? "/staff-app/login"
              : user.role === "service_partner" ? "/service-partner/login"
              : "/adentry";
  setErr(`This portal is restricted to administrators. Please use ${portal} for your account.`);
  return true;
}

export default function AdminEntry() {
  const { setUserAfterLogin } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState("creds"); // creds | otp
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [branding, setBranding] = useState(null);
  const [ip, setIp] = useState("");

  // 2FA state
  const [challengeId, setChallengeId] = useState("");
  const [otp, setOtp] = useState("");
  const [otpMessage, setOtpMessage] = useState("");
  const [ttl, setTtl] = useState(0);
  const [resendBusy, setResendBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/branding`).then((r) => setBranding(r.data)).catch(() => {});
    setIp(`${navigator.platform || "unknown"} · ${(navigator.language || "")}`.slice(0, 40));
    // Iter 237 — beacon /adentry mount for security audit (new-device
    // detection). Fingerprint uses stable browser+screen signals.
    try {
      const fp = `${navigator.userAgent || ""}|${navigator.platform || ""}|${screen.width}x${screen.height}|${(new Date()).getTimezoneOffset()}|${navigator.language || ""}`;
      const hash = Array.from(fp).reduce((a, c) => ((a << 5) - a + c.charCodeAt(0)) | 0, 0).toString(36);
      axios.post(`${API}/security/adentry-visit`, {
        fingerprint: `${hash}-${fp.length}`,
        screen: `${screen.width}x${screen.height}`,
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        lang: navigator.language,
        platform: navigator.platform,
        referrer: document.referrer || "",
      }).catch(() => { /* silent */ });
    } catch { /* ignore */ }
  }, []);

  // Countdown for OTP TTL
  useEffect(() => {
    if (step !== "otp" || ttl <= 0) return;
    const t = setInterval(() => setTtl((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [step, ttl]);

  const submitCreds = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const r = await api.post("/auth/login", { email, password });
      if (r.data.requires_otp) {
        setChallengeId(r.data.challenge_id);
        setOtpMessage(r.data.message || `A verification code was sent to ${r.data.email}.`);
        setTtl(r.data.ttl_seconds || 600);
        setOtp("");
        setStep("otp");
      } else {
        // Direct login (no 2FA required)
        const u = r.data.user;
        // Enforce role gate: /adentry is admin-only.
        if (rejectIfNotAdminEntry(u, setErr)) { setBusy(false); return; }
        localStorage.setItem("rescigo_token", r.data.access_token);
        localStorage.setItem("rescigo_user", JSON.stringify(u));
        setUserAfterLogin?.(u);
        const target = u?.role === "service_coordinator" ? "/coordinator" : "/dashboard";
        nav(target);
      }
    } catch (ex) {
      setErr(formatApiError(ex));
    }
    setBusy(false);
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const r = await api.post("/auth/login/verify-otp", {
        challenge_id: challengeId, otp: otp.trim(),
      });
      const u = r.data.user;
      // Enforce role gate after OTP success too.
      if (rejectIfNotAdminEntry(u, setErr)) {
        setStep("creds");
        setBusy(false);
        return;
      }
      localStorage.setItem("rescigo_token", r.data.access_token);
      localStorage.setItem("rescigo_user", JSON.stringify(u));
      setUserAfterLogin?.(u);
      const target = u?.role === "service_coordinator" ? "/coordinator" : "/dashboard";
      nav(target);
    } catch (ex) {
      setErr(formatApiError(ex));
    }
    setBusy(false);
  };

  const resendOtp = async () => {
    setErr(""); setResendBusy(true);
    try {
      const r = await api.post("/auth/login/resend-otp", {
        challenge_id: challengeId, otp: "",
      });
      setChallengeId(r.data.challenge_id);
      setTtl(r.data.ttl_seconds || 600);
      setOtp("");
      setOtpMessage("A new code has been emailed to you.");
    } catch (ex) {
      setErr(formatApiError(ex));
    }
    setResendBusy(false);
  };

  const goBackToCreds = () => {
    setStep("creds"); setOtp(""); setChallengeId(""); setErr("");
  };

  const ttlMm = String(Math.floor(ttl / 60)).padStart(2, "0");
  const ttlSs = String(ttl % 60).padStart(2, "0");

  return (
    <div className="min-h-screen flex bg-[#050C07] text-white" data-testid="admin-entry">
      {/* Left visual panel (unchanged) */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden border-r border-white/5">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(45,90,39,0.5),transparent_60%),radial-gradient(circle_at_70%_70%,rgba(230,57,70,0.25),transparent_55%)]" />
        <div className="absolute inset-0 opacity-30"
             style={{ backgroundImage: "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)", backgroundSize: "32px 32px" }} />
        <NetworkVisual />
        <div className="relative z-10 flex flex-col justify-between p-12 lg:p-16 w-full">
          <div className="flex items-center gap-3">
            <img src={branding?.logo_white_url || "/brand/rescigo-logo-white.png"} alt="Rescigo Health" className="h-10 w-auto opacity-95" data-testid="ae-logo" />
          </div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-[#5C9F4F] font-medium mb-4">
              <Pulse size={12} weight="fill" /> Operations Control Plane
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl tracking-tight leading-tight font-medium">
              The <span className="text-[#E63946]">command</span> layer<br />
              for India's smart waste<br />
              infrastructure.
            </h1>
            <p className="text-slate-300 mt-5 max-w-md text-sm leading-relaxed">
              Live ops · multi-tenant rails · CPCB compliance · 24/7 audit trail.
              Authorized personnel only.
            </p>
          </motion.div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 flex items-center gap-3">
            <ShieldCheck size={11} className="text-[#5C9F4F]" /> ISO 27001
            <span className="opacity-50">·</span> GDPR
            <span className="opacity-50">·</span> TLS 1.3
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col">
        <div className="flex items-center justify-between p-5 lg:p-7">
          <Link to="/" className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1.5" data-testid="ae-back">
            <ArrowLeft size={12} /> back to website
          </Link>
          <span className="text-[10px] text-slate-500 uppercase tracking-[0.2em] flex items-center gap-1.5">
            <Globe size={11} /> {ip}
          </span>
        </div>
        <div className="flex-1 flex items-center justify-center px-6 lg:px-12 py-10">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
                      className="w-full max-w-md">
            <div className="lg:hidden mb-6">
              <img src={branding?.logo_white_url || "/brand/rescigo-logo-white.png"} alt="Rescigo Health" className="h-9 w-auto opacity-95" />
            </div>
            {step === "creds" && (
              <>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E63946]/10 border border-[#E63946]/20 text-[10px] uppercase tracking-[0.22em] text-[#FF8A95] font-medium" data-testid="ae-restricted">
                  <LockKey size={11} weight="fill" /> Restricted access
                </div>
                <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium mt-5 leading-tight">
                  Authorized<br />
                  <span className="italic font-light text-[#E8F0E7]">operations personnel</span> only
                </h2>
                <p className="text-sm text-slate-400 mt-3">
                  Sign in with your assigned admin credentials. All sessions are IP-bound and audit-logged.
                </p>
                <form onSubmit={submitCreds} className="mt-8 space-y-4" data-testid="ae-form">
                  <Field
                    label="Work email" Icon={EnvelopeSimple}
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="firstname.lastname@yourdomain.com"
                    testid="ae-email" type="email" autoFocus
                  />
                  <Field
                    label="Password" Icon={LockKey}
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    testid="ae-password" type={showPwd ? "text" : "password"}
                    trailing={
                      <button type="button" onClick={() => setShowPwd((v) => !v)}
                              className="p-1.5 text-slate-500 hover:text-slate-200" tabIndex={-1}>
                        {showPwd ? <EyeSlash size={14} /> : <Eye size={14} />}
                      </button>
                    }
                  />
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 text-xs text-slate-400">
                      <input type="checkbox" className="accent-[#5C9F4F] w-3.5 h-3.5" /> Trusted device · 8h session
                    </label>
                    <a href="mailto:support@rescigo.health" className="text-xs text-slate-400 hover:text-white">Need access?</a>
                  </div>
                  {err && <div className="text-xs text-[#FF8A95] bg-[#E63946]/10 border border-[#E63946]/20 rounded-lg px-3 py-2" data-testid="ae-error">{err}</div>}
                  <button type="submit" disabled={busy} data-testid="admin-login-submit-btn"
                          className="w-full h-12 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white rounded-xl font-medium inline-flex items-center justify-center gap-2 shadow-lg shadow-green-900/30 transition">
                    {busy ? "Authenticating…" : "Continue"}
                    <ArrowRight size={14} weight="bold" />
                  </button>
                  <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-white/5">
                    <Sec label="MFA" status="ready" />
                    <Sec label="Audit" status="on" />
                    <Sec label="IP-bound" status="active" />
                  </div>
                  <div className="text-[10px] text-slate-500 text-center pt-2">
                    <Sparkle size={10} className="inline mr-1" />
                    Unauthorized access is monitored and prosecuted under the IT Act, 2000.
                  </div>
                </form>
              </>
            )}

            {step === "otp" && (
              <>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#5C9F4F]/15 border border-[#5C9F4F]/30 text-[10px] uppercase tracking-[0.22em] text-[#9CD18C] font-medium" data-testid="ae-step-otp">
                  <ShieldStar size={11} weight="fill" /> Two-step verification
                </div>
                <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium mt-5 leading-tight">
                  Enter the<br />
                  <span className="italic font-light text-[#E8F0E7]">8-digit code</span>
                </h2>
                <p className="text-sm text-slate-400 mt-3" data-testid="ae-otp-message">
                  {otpMessage}
                </p>
                <form onSubmit={submitOtp} className="mt-8 space-y-4" data-testid="ae-otp-form">
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-medium block mb-1.5">Verification code</label>
                    <input
                      autoFocus type="text" inputMode="numeric" maxLength={8} pattern="[0-9]{8}"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 8))}
                      placeholder="••••••••"
                      data-testid="ae-otp-input"
                      className="w-full h-14 px-4 bg-[#0A1410] border border-white/10 focus:border-[#5C9F4F] focus:ring-1 focus:ring-[#5C9F4F]/30 rounded-xl text-center text-2xl font-mono tracking-[0.4em] text-white placeholder:text-slate-700 outline-none transition"
                    />
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500" data-testid="ae-otp-ttl">
                      {ttl > 0 ? <>Code expires in <b className="text-slate-300">{ttlMm}:{ttlSs}</b></> : <span className="text-[#FF8A95]">Code expired</span>}
                    </span>
                    <button type="button" onClick={resendOtp} disabled={resendBusy}
                            data-testid="ae-otp-resend"
                            className="text-[#9CD18C] hover:text-white disabled:opacity-50 inline-flex items-center gap-1">
                      <ArrowsClockwise size={11} />
                      {resendBusy ? "Resending…" : "Resend code"}
                    </button>
                  </div>
                  {err && <div className="text-xs text-[#FF8A95] bg-[#E63946]/10 border border-[#E63946]/20 rounded-lg px-3 py-2" data-testid="ae-otp-error">{err}</div>}
                  <button type="submit" disabled={busy || otp.length !== 8} data-testid="ae-otp-submit"
                          className="w-full h-12 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white rounded-xl font-medium inline-flex items-center justify-center gap-2 shadow-lg shadow-green-900/30 transition">
                    {busy ? "Verifying…" : "Verify & sign in"}
                    <ArrowRight size={14} weight="bold" />
                  </button>
                  <button type="button" onClick={goBackToCreds} data-testid="ae-otp-back"
                          className="w-full text-xs text-slate-400 hover:text-white inline-flex items-center justify-center gap-1.5 pt-2">
                    <ArrowLeft size={11} /> use a different account
                  </button>
                  <div className="text-[10px] text-slate-500 text-center pt-2">
                    Never share this code. {`Rescigo Health staff will never ask for it.`}
                  </div>
                </form>
              </>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, Icon, trailing, testid, ...props }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-medium block mb-1.5">{label}</label>
    <div className="relative">
      <Icon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
      <input
        data-testid={testid}
        className="w-full h-12 pl-10 pr-10 bg-[#0A1410] border border-white/10 focus:border-[#5C9F4F] focus:ring-1 focus:ring-[#5C9F4F]/30 rounded-xl text-sm text-white placeholder:text-slate-600 outline-none transition"
        required
        {...props}
      />
      {trailing && <div className="absolute right-2 top-1/2 -translate-y-1/2">{trailing}</div>}
    </div>
  </div>
);

const Sec = ({ label, status }) => (
  <div className="flex items-center gap-1.5 text-[10px] text-slate-500 uppercase tracking-[0.18em]">
    <span className="w-1.5 h-1.5 bg-[#5C9F4F] rounded-full animate-pulse"></span>
    {label} · <span className="text-slate-300 normal-case tracking-normal">{status}</span>
  </div>
);

function NetworkVisual() {
  // Animated SVG network of operational nodes
  const nodes = Array.from({ length: 14 }, (_, i) => ({
    x: 20 + (i * 47) % 460, y: 60 + (i * 73) % 380, r: 3 + (i % 3),
    delay: (i * 0.18) % 2.4,
  }));
  return (
    <svg className="absolute inset-0 w-full h-full opacity-40" viewBox="0 0 500 500" preserveAspectRatio="xMidYMid slice">
      <defs>
        <radialGradient id="nodeG" cx="50%" cy="50%">
          <stop offset="0%" stopColor="#5C9F4F" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#5C9F4F" stopOpacity="0" />
        </radialGradient>
      </defs>
      {nodes.map((n, i) =>
        nodes.slice(i + 1).filter((m) => Math.hypot(m.x - n.x, m.y - n.y) < 130).map((m, j) => (
          <line key={`${i}-${j}`} x1={n.x} y1={n.y} x2={m.x} y2={m.y}
                stroke="rgba(92,159,79,0.15)" strokeWidth="0.8" />
        )),
      )}
      {nodes.map((n, i) => (
        <g key={i}>
          <circle cx={n.x} cy={n.y} r={n.r * 4} fill="url(#nodeG)">
            <animate attributeName="r" values={`${n.r * 2};${n.r * 5};${n.r * 2}`} dur="3.2s" begin={`${n.delay}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.2;0.7;0.2" dur="3.2s" begin={`${n.delay}s`} repeatCount="indefinite" />
          </circle>
          <circle cx={n.x} cy={n.y} r={n.r} fill="#5C9F4F" />
        </g>
      ))}
    </svg>
  );
}
